/*
  Cheerful Buddha add to basket and slide-out basket (Phase 1, Backend agent).
  Loaded once with defer from snippets/cb-head.liquid. No library, no build step.

  One confirmation for every add (James, 9 Oct 2026): the slide-out basket. It is Horizon's cart drawer
  (snippets/cart-drawer.liquid, Theme settings > Cart: Cart type Drawer) with the Cheerful Buddha parts from
  snippets/cb-cart-drawer.liquid (free UK delivery bar, first-order note, add-ons, express checkout, a live
  region). It overlays the page at every width (the `modal` attribute, assets/theme-drawer.js): focus moves
  into it (to its heading) and is trapped there, Escape and the backdrop close it, and focus returns to the
  button that opened it. There is no other "added" message: no toast, no banner.

  Any <form data-cb-add-form> (product page buy box and sticky bar, product cards in every grid, pairs,
  bundles, the drawer's add-ons) is sent with the Cart AJAX API:
    1. POST {form action}.js (normally /cart/add.js) with the form's own fields (id, quantity,
       selling_plan, properties[...]) plus sections=<the drawer's sections> (Section Rendering API), so the
       answer carries the re-rendered drawer. The button is marked aria-busy while it runs (dimmed, with a
       spinner: the STYLE below).
    2. GET /cart.js, write item_count into every [data-cb-cart-count], refresh the aria-label of
       every [data-cb-cart-label] ("{count}" is replaced), update every [data-cb-free-delivery] on the
       page, then dispatch document 'cb:cart-updated' with detail {item_count, cart, item, form}.
    3. Dispatch Shopify's standard 'shopify:cart:lines-update' event (action 'add', from
       @shopify/events, as Horizon's own product form does) with the new basket and the drawer HTML:
       Horizon's drawer re-renders, Horizon and the cb header update their counts, and apps that follow
       the standard cart events see the add. The drawer opens (by itself with Theme settings > Cart >
       Auto-open drawer, otherwise this file opens it). "Added to basket" (the form's data-cb-added-text,
       else the drawer's) is announced from the drawer's live region. An add from inside the drawer (an
       add-on) moves focus to the new line.
  No drawer on the page (Cart type Page, or the basket page itself): the form is not intercepted, so
  Shopify adds the item and shows the basket page. The same happens if the drawer's scripts have not
  loaded a few seconds after a successful add: the shopper is taken to the basket page, never left
  without a confirmation and never shown two.
  Errors: a 4xx answer (for example 422 sold out or "You can't add more") shows Shopify's own message by
  the button (the form's [data-cb-add-error] element, one beside the form, or one made for it), announced
  through an assertive live region, and dispatches 'cb:cart-error'. The drawer never opens for a failed add.
  Never twice: once Shopify has answered, the normal form post is never used, even when reading the answer
  fails (WebKit and Firefox abort it when the shopper leaves mid-add); an answer that can't be read shows the
  error message and the count is refreshed. While the page is unloading nothing is re-sent. Only when the
  request never reached Shopify (offline, blocked) is the form submitted normally (POST /cart/add), so the
  purchase still goes through without JavaScript help.

  Horizon's own adds (quick add, if a Horizon product form is ever used) open the drawer themselves; this
  file announces them. The header basket link ([data-cb-cart-link]) opens the drawer instead of going to
  /cart (the link still works without JavaScript, with a modifier key, and on the basket page).

  Product cards with a size choice (snippets/cb-product-card.liquid, radios [data-cb-card-variant]): this
  file shows the chosen size's price (and subscription price) on the card.

  Free UK delivery line (snippets/cb-free-delivery.liquid renders it; this file keeps it current): every
  [data-cb-free-delivery] on the page (the basket page section and the drawer) is redrawn from /cart.js when
  the basket changes: after an add from a cb form, and after Horizon's shopify:cart:lines-update (quantity,
  remove and quick add) and shopify:cart:discount-update events. It hides while the basket is empty, holds
  a subscription (unless the section allows it) or is in another currency than the store's. No threshold,
  no line: the amount is never guessed. Free with every bundle (James, 13 Oct 2026): while /cart.js has a
  one-time line (no selling_plan_allocation) whose product_type matches data-cb-fd-type (Theme settings >
  Cheerful Buddha > "Product type that always gets free UK delivery", default Bundle; blank = off), the
  line shows its reached state with the bar full, whatever the total (snippets/cb-free-delivery-type.liquid),
  and the line under the total ([data-cb-fd-note]) swaps to its free-delivery wording ("VAT included.").

  Bundle swap (the drawer's "Swap to the ... bundle" line, snippets/cb-cart-drawer.liquid): a form
  [data-cb-add-form] whose action is /cart/update, with updates[<line key>]=<one fewer> for each item it
  replaces and updates[<bundle variant>]=1. It is sent the same way to /cart/update.js (one request, so the
  items and the bundle change together), the drawer re-renders, "Swapped to the bundle" is announced and focus
  moves to the bundle's line (data-cb-swap-variant). Without JavaScript Shopify does the same and shows /cart.

  Optional form attributes: data-cb-added-text (announced when the drawer opens), data-cb-error-text
  (message when Shopify sends none), data-cb-cart-url (basket page, default the locale's /cart).
  The English fallbacks below are used only when neither the form nor the drawer carries the wording.
*/
(() => {
  if (window.cbCart) return;

  const FALLBACK = {
    added: 'Added to basket',
    error: "Sorry, we couldn't add that to your basket. Please try again.",
  };
  const DRAWER_SELECTOR = 'theme-drawer#cart-drawer';
  const LINES_UPDATE = 'shopify:cart:lines-update';
  const FD_SELECTOR = '[data-cb-free-delivery]';
  const NOTE_SELECTOR = '[data-cb-fd-note]';
  const AMOUNT_TOKEN = '[amount]';
  const DRAWER_WAIT_MS = 4000;

  const STYLE = `
.cb-cart-live{position:absolute!important;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;border:0}
.cb-add-error{margin:10px 0 0;padding:10px 14px;border-radius:14px;background:#fbe3d6;color:var(--cb-brown);font-size:15px;font-weight:700;line-height:1.4}
.cb-add-error[hidden]{display:none}
form[data-cb-busy] [aria-busy="true"]{cursor:progress}
:is(.cb-card__add,.cb-pdp__add,.cb-pdp__sticky-add,.cb-drawer-addon__add,.cb-pdp-bundles__add,.cb-drawer-bundle__add)[aria-busy="true"]{opacity:.6;cursor:progress}
:is(.cb-card__add,.cb-pdp__add,.cb-pdp__sticky-add,.cb-drawer-addon__add,.cb-pdp-bundles__add,.cb-drawer-bundle__add)[aria-busy="true"]::after{content:"";display:inline-block;width:.95em;height:.95em;margin-left:.55em;vertical-align:-.15em;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:cb-add-spin .7s linear infinite}
@keyframes cb-add-spin{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){:is(.cb-card__add,.cb-pdp__add,.cb-pdp__sticky-add,.cb-drawer-addon__add,.cb-pdp-bundles__add,.cb-drawer-bundle__add)[aria-busy="true"]::after{animation:none;border-right-color:currentColor;opacity:.5}}
`;

  const root = () => {
    const value = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return value.endsWith('/') ? value : `${value}/`;
  };

  const addUrl = (form) => {
    const action = (form.getAttribute('action') || '').split(/[?#]/)[0];
    if (/\/cart\/(add|update)\/?$/.test(action)) return `${action.replace(/\/$/, '')}.js`;
    return `${root()}cart/add.js`;
  };

  const cartUrl = (form) => (form && form.getAttribute('data-cb-cart-url')) || `${root()}cart`;

  const copy = (form, attribute, fallback) => {
    const value = form ? form.getAttribute(attribute) : null;
    return value && value.trim() ? value.trim() : fallback;
  };

  const decoder = document.createElement('textarea');
  const cleanMessage = (value) => {
    if (typeof value !== 'string' || !value.trim()) return '';
    decoder.innerHTML = value;
    return decoder.value.replace(/<[^>]*>/g, '').trim();
  };

  /* Money: the store's format (e.g. "<span class=money>£{{amount}}</span>") without its HTML. */

  const formatMoney = (cents, format) => {
    const fmt = cleanMessage(String(format || '')) || '£{{amount}}';
    const value = Math.max(0, Math.round(Number(cents) || 0));
    const delimit = (precision, thousands, decimal) => {
      const [whole, fraction] = (value / 100).toFixed(precision).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };
    const token = /\{\{\s*(\w+)\s*\}\}/;
    const match = fmt.match(token);
    if (!match) return `£${delimit(2, ',', '.')}`;
    let amount;
    switch (match[1]) {
      case 'amount_no_decimals':
        amount = delimit(0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        amount = delimit(2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = delimit(0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = delimit(2, "'", '.');
        break;
      case 'amount_with_space_separator':
        amount = delimit(2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        amount = delimit(0, ' ', '');
        break;
      case 'amount_with_period_and_space_separator':
        amount = delimit(2, ' ', '.');
        break;
      default:
        amount = delimit(2, ',', '.');
    }
    return fmt.replace(token, amount);
  };

  /* Free UK delivery line */

  const readFreeDelivery = (el) => {
    if (!el || typeof el.getAttribute !== 'function') return null;
    const threshold = parseInt(el.getAttribute('data-cb-fd-threshold') || '', 10);
    if (!Number.isFinite(threshold) || threshold <= 0) return null;
    return {
      threshold,
      currency: el.getAttribute('data-cb-fd-currency') || '',
      format: el.getAttribute('data-cb-fd-money-format') || '',
      remaining: el.getAttribute('data-cb-fd-remaining') || '',
      reached: el.getAttribute('data-cb-fd-reached') || '',
      hideSubscription: el.getAttribute('data-cb-fd-hide-subscription') === 'true', // subscriptions: same £30 rule
      freeType: (el.getAttribute('data-cb-fd-type') || '').trim().toLowerCase(), // e.g. "bundle"; '' = off
    };
  };

  // A one-time line of the free-delivery product type (a Shopify Bundles line is the bundle product itself).
  const hasFreeDeliveryLine = (items, type) =>
    !!type &&
    items.some(
      (item) => item && !item.selling_plan_allocation && String(item.product_type || '').trim().toLowerCase() === type
    );

  // {hidden} or {hidden: false, reached, left (pence), percent (0-100), amount (formatted left)}
  const freeDeliveryState = (cart, config) => {
    if (!cart || !config) return null;
    const items = Array.isArray(cart.items) ? cart.items : [];
    const count = Number(cart.item_count) || 0;
    const otherCurrency = !!(config.currency && cart.currency && cart.currency !== config.currency);
    const subscription = config.hideSubscription && items.some((item) => item && item.selling_plan_allocation);
    if (count <= 0 || otherCurrency || subscription) return { hidden: true };
    const total = Math.max(0, Number(cart.total_price) || 0);
    const left = hasFreeDeliveryLine(items, config.freeType) ? 0 : Math.max(0, config.threshold - total);
    const reached = left === 0;
    const percent = reached ? 100 : Math.max(0, Math.min(99, Math.floor((total * 100) / config.threshold)));
    return { hidden: false, reached, left, percent, amount: formatMoney(left, config.format) };
  };

  const plainText = (template, amount) => String(template || '').split(AMOUNT_TOKEN).join(amount);

  // Text with the amount in <strong>, built from text nodes (the wording is never parsed as HTML).
  const fillText = (target, template, amount, strongClass) => {
    target.textContent = '';
    String(template || '')
      .split(AMOUNT_TOKEN)
      .forEach((part, index) => {
        if (index > 0) {
          const strong = document.createElement('strong');
          if (strongClass) strong.className = strongClass;
          strong.textContent = amount;
          target.appendChild(strong);
        }
        if (part) target.appendChild(document.createTextNode(part));
      });
  };

  const setProgress = (bar, percent) => {
    if (!bar) return;
    bar.setAttribute('aria-valuenow', String(percent));
    bar.style.setProperty('--cb-fd-progress', String(percent));
  };

  const renderFreeDeliveryElement = (el, cart) => {
    const config = readFreeDelivery(el);
    const state = freeDeliveryState(cart, config);
    if (!state) return;
    if (state.hidden) {
      el.hidden = true;
      return;
    }
    const next = state.reached ? 'reached' : 'remaining';
    const flipped = el.getAttribute('data-cb-fd-state') !== next;
    el.setAttribute('data-cb-fd-state', next);
    setProgress(el.querySelector('[role="progressbar"]'), state.percent);
    el.hidden = false;
    // Both messages share one grid cell (no height change); CSS shows the one for the state. The
    // shown one is rewritten only when its words change or the state flips, so the role="status"
    // line announces each change once (a text change is announced more reliably than a CSS swap).
    const remaining = el.querySelector('[data-cb-fd-text="remaining"]');
    const reached = el.querySelector('[data-cb-fd-text="reached"]');
    if (state.reached) {
      if (reached && flipped) reached.textContent = config.reached;
    } else if (remaining && (flipped || remaining.textContent !== plainText(config.remaining, state.amount))) {
      fillText(remaining, config.remaining, state.amount, 'cb-free-delivery__amount');
    }
  };

  // The line under the basket total (snippets/cb-cart-drawer-summary.liquid): the free-delivery wording ("VAT
  // included.") while a bundle gets the basket free UK delivery, else the standard one quoting the £1.99.
  const renderDeliveryNote = (note, items) => {
    const free = hasFreeDeliveryLine(items, (note.getAttribute('data-cb-fd-type') || '').trim().toLowerCase());
    const standard = note.querySelector('[data-cb-fd-note-text="standard"]');
    const freeText = note.querySelector('[data-cb-fd-note-text="free"]');
    if (standard) standard.hidden = free;
    if (freeText) freeText.hidden = !free;
    note.hidden = !(free ? freeText : standard);
  };

  const renderFreeDelivery = (cart) => {
    if (!cart) return;
    document.querySelectorAll(FD_SELECTOR).forEach((el) => renderFreeDeliveryElement(el, cart));
    const items = Array.isArray(cart.items) ? cart.items : [];
    document.querySelectorAll(NOTE_SELECTOR).forEach((note) => renderDeliveryNote(note, items));
  };

  const hasFreeDelivery = () => !!document.querySelector(`${FD_SELECTOR}, ${NOTE_SELECTOR}`);

  /* Styles and live regions */

  const ensureStyles = () => {
    if (document.getElementById('cb-cart-style')) return;
    const style = document.createElement('style');
    style.id = 'cb-cart-style';
    style.textContent = STYLE;
    document.head.appendChild(style);
  };

  const regions = {};
  const ensureRegions = () => {
    if (regions.polite || !document.body) return;
    ensureStyles();
    const make = (role, live) => {
      const el = document.createElement('div');
      el.className = 'cb-cart-live';
      el.setAttribute('role', role);
      el.setAttribute('aria-live', live);
      el.setAttribute('aria-atomic', 'true');
      document.body.appendChild(el);
      return el;
    };
    regions.polite = make('status', 'polite');
    regions.assertive = make('alert', 'assertive');
  };

  const announce = (message, assertive) => {
    ensureRegions();
    const region = assertive ? regions.assertive : regions.polite;
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 120);
  };

  /* Basket drawer (Horizon's cart drawer, see the top of this file) */

  const cartDrawer = () => document.querySelector(DRAWER_SELECTOR);

  // The drawer and the components that re-render and open it are defined (their modules have run).
  const drawerReady = () => {
    const drawer = cartDrawer();
    if (!drawer || typeof drawer.open !== 'function') return null;
    if (!window.customElements || !customElements.get('cart-drawer-component') || !customElements.get('cart-items-component')) return null;
    return drawer;
  };

  // Resolves with the drawer once its scripts have run (a tap on a slow phone can beat them), or null
  // after DRAWER_WAIT_MS.
  const whenDrawerReady = () => {
    const ready = drawerReady();
    if (ready || !cartDrawer() || !window.customElements) return Promise.resolve(ready);
    const defined = Promise.all(
      ['theme-drawer', 'cart-drawer-component', 'cart-items-component'].map((name) => customElements.whenDefined(name))
    ).then(() => drawerReady());
    const timeout = new Promise((resolve) => window.setTimeout(() => resolve(drawerReady()), DRAWER_WAIT_MS));
    return Promise.race([defined, timeout]);
  };

  // Horizon opens the drawer by itself after an add only with Theme settings > Cart > Auto-open drawer.
  const drawerAutoOpens = (drawer) => !!drawer && !!drawer.querySelector('cart-drawer-component[auto-open]');

  const openDrawerDialog = () => {
    const drawer = cartDrawer();
    const dialog = drawer && drawer.hasAttribute('open') ? drawer.querySelector('dialog') : null;
    return dialog && dialog.open ? dialog : null;
  };

  // Sections that show the basket on this page (the drawer): re-rendered with every add. Read from the
  // markup, so they are known before the drawer's scripts have run.
  const basketSectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((el) => el.getAttribute('data-section-id'))
      .filter(Boolean);

  const drawerLive = () => document.querySelector(`${DRAWER_SELECTOR} [data-cb-drawer-live]`);

  // Read out from inside the dialog (the page behind an open modal dialog is inert).
  const announceInDrawer = (message) => {
    const region = drawerLive();
    if (!region) return announce(message, false);
    if (!message) return undefined;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 150);
    return undefined;
  };

  // Wording from the drawer (Theme settings > Cheerful Buddha > Basket drawer).
  const drawerText = (name, fallback) => {
    const region = drawerLive();
    const text = region ? region.getAttribute(name) : '';
    return (text && text.trim()) || fallback;
  };

  const drawerAddedText = (form) => copy(form, 'data-cb-added-text', drawerText('data-cb-added-text', FALLBACK.added));

  // Shopify's standard cart events (@shopify/events, mapped in snippets/scripts.liquid). Loaded once; a
  // plain event with the same fields stands in if the module cannot load.
  let standardEvents = null;
  let standardEventsLoading = null;
  const loadStandardEvents = () => {
    if (!standardEventsLoading) {
      standardEventsLoading = import('@shopify/events')
        .then((module) => {
          standardEvents = module;
          return module;
        })
        .catch(() => null);
    }
    return standardEventsLoading;
  };

  const ownEvents = new WeakSet();

  const linesUpdateEvent = (init) => {
    const LinesUpdate = standardEvents && standardEvents.CartLinesUpdateEvent;
    let event;
    if (LinesUpdate) {
      event = new LinesUpdate(init);
    } else {
      event = new Event(LINES_UPDATE, { bubbles: true, cancelable: true });
      Object.assign(event, init);
    }
    ownEvents.add(event);
    return event;
  };

  const standardCart = (cart) => {
    const LinesUpdate = standardEvents && standardEvents.CartLinesUpdateEvent;
    if (cart && LinesUpdate && typeof LinesUpdate.createCartFromAjaxResponse === 'function') {
      try {
        return LinesUpdate.createCartFromAjaxResponse(cart);
      } catch (error) {
        // fall through to the minimal shape
      }
    }
    return cart ? { totalQuantity: cart.item_count } : {};
  };

  // After an add from inside the drawer, the add-on (or bundle suggestion) the shopper pressed disappears when the drawer
  // re-renders: move focus to the new line (else the close button) instead of losing it.
  const focusAddedLine = (variantId) => {
    const dialog = openDrawerDialog();
    if (!dialog) return;
    let tries = 0;
    const settled = () =>
      !dialog.querySelector(
        `:is(.cb-drawer-addons, .cb-drawer-bundle) input[name="id"][value="${CSS.escape(String(variantId))}"]`
      );
    const target = () =>
      dialog.querySelector(`.cart-items__table-row[data-key^="${CSS.escape(String(variantId))}:"] .cart-items__title`) ||
      dialog.querySelector('.theme-drawer__close-button');
    const lost = () => !dialog.contains(document.activeElement) || document.activeElement === document.body;
    const tick = () => {
      if (!dialog.open) return;
      if (!settled() && tries++ < 40) {
        window.setTimeout(tick, 50);
        return;
      }
      const el = target();
      if (el && document.activeElement !== el) el.focus();
      // A late re-render (the drawer settling its layout) can drop focus again: check once more.
      window.setTimeout(() => {
        const again = target();
        if (dialog.open && again && lost()) again.focus();
      }, 400);
    };
    window.setTimeout(tick, 50);
  };

  // Tell Horizon (drawer, counts) and any app about an add that already succeeded. Without the drawer's
  // HTML (or the basket) Horizon re-renders the drawer itself.
  const dispatchAdded = (form, item, cart, sections, quantity) => {
    const variantId = (item && (item.variant_id || item.id)) || null;
    const lines = variantId ? [{ merchandiseId: String(variantId), quantity: quantity || (item && item.quantity) || 1 }] : [];
    const result = {
      cart: standardCart(cart),
      detail: {
        items: cart ? cart.items : [],
        itemCount: quantity || 1,
        source: 'cb-cart',
        sections: sections || undefined,
        didError: false,
      },
    };
    const event = linesUpdateEvent({ action: 'add', context: 'product', lines, promise: Promise.resolve(result) });
    // From the form (Horizon checks where an add came from); from the page if the form was re-rendered away.
    (form.isConnected ? form : document).dispatchEvent(event);
  };

  // Horizon pins the drawer's summary (total and Check out) only when it measured the summary at under
  // half the drawer's height, and it measures when the drawer opens. On a first add (empty basket) it
  // opens before the new lines are in, measures nothing and leaves the summary unpinned, so on a phone
  // Check out can sit below the fold. Re-measure whenever the drawer's content changes, through the
  // event Horizon's drawer already listens to for this ('cart-section:restored', assets/events.js).
  const watchDrawerLayout = () => {
    const component = document.querySelector(`${DRAWER_SELECTOR} cart-drawer-component`);
    if (!component || !window.MutationObserver) return;
    let timer = null;
    let late = null;
    const inTransition = () => {
      try {
        return document.documentElement.matches(':active-view-transition');
      } catch (error) {
        return false; // browsers without view transitions
      }
    };
    // Sizes taken mid-transition are the old snapshot's: wait for the transition to end (up to ~3s).
    const measure = (tries = 0) => {
      if (inTransition() && tries < 15) {
        window.setTimeout(() => measure(tries + 1), 200);
        return;
      }
      component.dispatchEvent(new Event('cart-section:restored', { bubbles: true }));
    };
    new MutationObserver(() => {
      window.clearTimeout(timer);
      window.clearTimeout(late);
      timer = window.setTimeout(measure, 120);
      // Again once Horizon's empty-to-filled view transition (Safari, Chrome) has finished.
      late = window.setTimeout(measure, 700);
    }).observe(component, { childList: true, subtree: true });
  };

  // The basket link opens the drawer (a plain link without JavaScript, with a modifier key, or before
  // Horizon's drawer has loaded). aria-controls also tells the drawer where to return focus.
  const wireBasketLinks = () => {
    if (!cartDrawer()) return;
    document.querySelectorAll('[data-cb-cart-link]').forEach((link) => {
      link.setAttribute('aria-haspopup', 'dialog');
      link.setAttribute('aria-controls', 'cart-drawer');
    });
  };

  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-cb-cart-link]') : null;
    if (!link || event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const drawer = drawerReady();
    if (!drawer) return;
    event.preventDefault();
    drawer.open();
  });

  // First focus goes to the drawer's title (a heading, so no focus ring on a tap; screen readers read
  // "Basket" and the count), not to Horizon's close button; Tab then moves through the drawer as usual.
  const drawerTitle = () => {
    const dialog = openDrawerDialog();
    const title = dialog && dialog.querySelector('#cart-drawer-heading');
    if (title && !title.hasAttribute('tabindex')) title.setAttribute('tabindex', '-1');
    return title;
  };

  const focusDrawerTitle = () => {
    const title = drawerTitle();
    if (title) title.focus({ preventScroll: true });
  };

  // The drawer re-renders right after it opens on an add (a slow phone can take a few seconds): put focus back
  // on the title if that dropped it, for 4 seconds.
  const guardDrawerFocus = () => {
    let checks = 0;
    let wasOpen = false;
    const check = () => {
      const dialog = openDrawerDialog();
      // Closed again by the shopper: stop. Not open yet (it can open late on a first add, after Horizon's
      // empty-to-filled transition): keep waiting.
      if (!dialog && wasOpen) return;
      if (dialog) {
        wasOpen = true;
        if (!dialog.contains(document.activeElement) || document.activeElement === document.body) focusDrawerTitle();
      }
      if (++checks < 27) window.setTimeout(check, 150);
    };
    window.setTimeout(check, 100);
  };

  // Removing a line takes its remove button away with it: move focus to the line that takes its place
  // (else the one before), or to the drawer's title once the basket is empty, not to the page.
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest(`${DRAWER_SELECTOR} .cart-items__remove`) : null;
    const dialog = openDrawerDialog();
    if (!button || !dialog) return;
    const rows = () =>
      Array.from(dialog.querySelectorAll('tbody .cart-items__table-row:not(.removing):not(.cart-items__nested-line)'));
    const row = button.closest('.cart-items__table-row');
    const index = Math.max(0, rows().indexOf(row));
    let checks = 0;
    const check = () => {
      if (!dialog.open) return;
      const active = document.activeElement;
      const lost = !active || active === document.body || !dialog.contains(active) || !active.isConnected;
      if (lost) {
        const left = rows();
        const next = left[index] || left[index - 1] || left[left.length - 1];
        const target = (next && next.querySelector('.cart-items__title')) || drawerTitle();
        if (target) target.focus({ preventScroll: false });
      }
      if (++checks < 16) window.setTimeout(check, 150);
    };
    window.setTimeout(check, 100);
  });

  document.addEventListener('theme-drawer:open', (event) => {
    if (!(event.target instanceof Element) || !event.target.matches(DRAWER_SELECTOR)) return;
    focusDrawerTitle();
  });

  /* Cart count and basket state */

  const updateCount = (count) => {
    if (typeof count !== 'number' || !Number.isFinite(count)) return;
    const value = String(count);
    document.querySelectorAll('[data-cb-cart-count]').forEach((el) => {
      if (el.textContent !== value) el.textContent = value;
      el.setAttribute('data-cb-cart-count', value);
    });
    document.querySelectorAll('[data-cb-cart-label]').forEach((el) => {
      const one = el.getAttribute('data-cb-cart-label-one');
      const template = count === 1 && one ? one : el.getAttribute('data-cb-cart-label');
      if (template) el.setAttribute('aria-label', template.replace(/\{\{\s*count\s*\}\}|\{count\}|\[count\]/g, value));
    });
  };

  const getCart = () =>
    fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(
      (response) => {
        if (!response.ok) throw new Error(`cart.js ${response.status}`);
        return response.json();
      }
    );

  // Answers can arrive out of order (quick quantity changes): only the newest request is drawn.
  let cartRequest = 0;
  const fetchCart = () => {
    const request = ++cartRequest;
    return getCart().then((cart) => ({ cart, latest: request === cartRequest }));
  };

  const applyCart = (cart) => {
    if (!cart) return;
    updateCount(cart.item_count);
    renderFreeDelivery(cart);
  };

  const refresh = () =>
    fetchCart().then(({ cart, latest }) => {
      if (latest) applyCart(cart);
      return cart;
    });

  /* Busy state: aria-disabled rather than disabled, so keyboard focus stays on the button. */

  const setBusy = (form, button, busy) => {
    if (busy) form.setAttribute('data-cb-busy', '');
    else form.removeAttribute('data-cb-busy');
    if (!button) return;
    if (busy) {
      button.setAttribute('data-cb-prev-aria-disabled', button.getAttribute('aria-disabled') || '');
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');
    } else if (button.hasAttribute('data-cb-prev-aria-disabled')) {
      const previous = button.getAttribute('data-cb-prev-aria-disabled');
      button.removeAttribute('data-cb-prev-aria-disabled');
      if (previous) button.setAttribute('aria-disabled', previous);
      else button.removeAttribute('aria-disabled');
      button.removeAttribute('aria-busy');
    }
  };

  // The form's id attribute: form.id would return the product form's <input name="id"> instead.
  const formId = (form) => form.getAttribute('id') || '';

  // Where a failed add says why: inside the form, beside it (drawer add-ons), or tied to it by id.
  const findErrorSlot = (form) =>
    form.querySelector('[data-cb-add-error]') ||
    (form.parentElement ? form.parentElement.querySelector(':scope > [data-cb-add-error]') : null) ||
    (formId(form) ? document.querySelector(`[data-cb-add-error="${CSS.escape(formId(form))}"]`) : null);

  // A form without one (a new section that forgot it) gets one at its end, so the message is never lost.
  const errorSlot = (form) => {
    let slot = findErrorSlot(form);
    if (!slot) {
      ensureStyles();
      slot = document.createElement('p');
      slot.className = 'cb-add-error';
      slot.setAttribute('data-cb-add-error', '');
      form.appendChild(slot);
    }
    return slot;
  };

  const clearError = (form) => {
    const slot = findErrorSlot(form);
    if (slot) {
      slot.textContent = '';
      slot.hidden = true;
    }
  };

  const nativeSubmit = (form) => {
    form.setAttribute('data-cb-native-submit', '');
    HTMLFormElement.prototype.submit.call(form);
  };

  /* Outcomes */

  const onAdded = (form, item, cart, latest, sections, quantity, fromDrawer) => {
    const count = cart && typeof cart.item_count === 'number' ? cart.item_count : null;
    if (cart && latest !== false) applyCart(cart);
    document.dispatchEvent(
      new CustomEvent('cb:cart-updated', { detail: { item_count: count, cart: cart || null, item: item || null, form } })
    );
    return whenDrawerReady().then((drawer) => {
      if (!drawer) {
        // The item is in the basket but the drawer can't show it: the basket page is the confirmation.
        window.location.assign(cartUrl(form));
        return;
      }
      const autoOpens = drawerAutoOpens(drawer);
      dispatchAdded(form, item, cart, sections, quantity);
      if (!autoOpens && !openDrawerDialog()) drawer.open();
      if (!cart) refresh().catch(() => {});
      announceInDrawer(drawerAddedText(form));
      if (fromDrawer && item) focusAddedLine(item.variant_id || item.id);
      else guardDrawerFocus();
    });
  };

  const onError = (form, data) => {
    const message =
      cleanMessage(data && data.description) ||
      cleanMessage(data && data.message) ||
      copy(form, 'data-cb-error-text', drawerText('data-cb-error-text', FALLBACK.error));
    document.dispatchEvent(
      new CustomEvent('cb:cart-error', { detail: { message, status: data ? data.status : null, form } })
    );
    // Inside the open drawer the page's live regions are inert: the drawer's own region reads it out.
    const dialog = openDrawerDialog();
    if (dialog && dialog.contains(form)) announceInDrawer(message);
    else announce(message, true);
    const slot = errorSlot(form);
    slot.textContent = message;
    slot.hidden = false;
  };

  const isErrorBody = (data) =>
    !!data && data.status !== undefined && !data.key && !data.items && !!(data.description || data.message);

  const add = (form, submitter) => {
    if (form.hasAttribute('data-cb-busy')) return;
    const button =
      submitter ||
      form.querySelector('[type="submit"]') ||
      (formId(form) ? document.querySelector(`[type="submit"][form="${CSS.escape(formId(form))}"]`) : null);
    let body;
    try {
      body = submitter ? new FormData(form, submitter) : new FormData(form);
    } catch (error) {
      body = new FormData(form);
    }
    // Section Rendering API: Shopify re-renders the basket drawer after the add (Horizon morphs it in).
    const quantity = Math.max(1, parseInt(body.get('quantity'), 10) || 1);
    const drawerSections = basketSectionIds();
    if (!body.has('sections') && drawerSections.length) {
      body.append('sections', drawerSections.join(','));
      body.append('sections_url', window.location.pathname);
    }

    clearError(form);
    // An add-on inside the open drawer: the form is gone once the drawer re-renders, so decide now.
    const fromDrawer = !!(openDrawerDialog() && openDrawerDialog().contains(form));
    setBusy(form, button, true);
    // Never send an item twice (Technical QA, 9 Oct 2026): once Shopify has answered, the add has been
    // handled (or refused) on the server, so the normal form post is never a fallback after that, even if
    // reading the answer fails. WebKit and Firefox abort that read when the shopper leaves the page mid-add.
    let replied = false;

    fetch(addUrl(form), {
      method: 'POST',
      body,
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
    })
      .then((response) => {
        replied = true;
        return response.text().then(
          (raw) => {
            let data = null;
            try {
              data = raw ? JSON.parse(raw) : null;
            } catch (error) {
              data = null;
            }
            return { ok: response.ok, status: response.status, data };
          },
          () => ({ ok: response.ok, status: response.status, data: null })
        );
      })
      .then((result) => {
        // The answer came while the page seemed to be going away (beforeunload). It may stay after all (a
        // mailto, tel or download link, a cancelled navigation): free the button now, then once the leaving
        // window has passed, if the page is still here, show the result as usual.
        if (pageLeaving()) {
          setBusy(form, button, false);
          const epoch = pageEpoch;
          return new Promise((resolve) => {
            window.setTimeout(resolve, Math.max(0, LEAVING_MS - (Date.now() - leavingAt)) + 100);
          }).then(() => (pageLeaving() || epoch !== pageEpoch ? undefined : handleResult(result)));
        }
        return handleResult(result);
      })
      .catch(() => {
        setBusy(form, button, false);
        // A failure after Shopify answered (a script error while showing the basket): the item is in the
        // basket, so only bring the count up to date.
        if (replied) {
          if (!pageLeaving()) refresh().catch(() => {});
          return;
        }
        // No answer. WebKit rejects the request the moment the shopper starts leaving (back, a link), just
        // before beforeunload fires, so decide a moment later: leaving, or back from the back/forward cache
        // since, means never re-send. Otherwise nothing reached Shopify (offline, request blocked): the normal
        // form post still gets the item added.
        const epoch = pageEpoch;
        window.setTimeout(() => {
          if (epoch !== pageEpoch || pageHidden) return;
          if (pageLeaving()) {
            // beforeunload fired but the page may stay: once the window has passed, say the add didn't go
            // through (it may have reached Shopify, so never re-send) and redraw the count.
            window.setTimeout(() => {
              if (epoch !== pageEpoch || pageLeaving()) return;
              onError(form, null);
              refresh().catch(() => {});
            }, Math.max(0, LEAVING_MS - (Date.now() - leavingAt)) + 100);
            return;
          }
          nativeSubmit(form);
        }, 500);
      });

    const handleResult = (result) => {
      if (result.ok && !isErrorBody(result.data)) {
        setBusy(form, button, false);
        let item = result.data;
        let sections = null;
        if (item && typeof item === 'object' && 'sections' in item) {
          sections = item.sections && typeof item.sections === 'object' ? item.sections : null;
          item = { ...item };
          delete item.sections;
        }
        // A bundle swap (/cart/update.js answers with the whole basket): the new line is the bundle's.
        const swapVariant = form.getAttribute('data-cb-swap-variant');
        if (swapVariant && item && Array.isArray(item.items)) {
          item = item.items.find((line) => String(line.variant_id) === swapVariant) || null;
        }
        // The standard events module is normally loaded long before; never hold the basket up for it.
        const eventsReady = Promise.race([loadStandardEvents(), new Promise((resolve) => window.setTimeout(resolve, 800))]);
        return Promise.all([fetchCart(), eventsReady]).then(
          ([{ cart, latest }]) => onAdded(form, item, cart, latest, sections, quantity, fromDrawer),
          () => onAdded(form, item, null, false, sections, quantity, fromDrawer)
        );
      }
      // A refusal (4xx: sold out, "You can't add more") or an answer we can't read (5xx, not JSON): say so by
      // the button, and redraw the count in case the server added it anyway. No second post.
      setBusy(form, button, false);
      onError(form, result.data);
      if (!result.data || !(result.status >= 400 && result.status < 500)) refresh().catch(() => {});
      return undefined;
    };
  };

  // The page is going away (a link, back, reload): an add still in flight is left alone, never re-sent.
  const LEAVING_MS = 3000;
  let leavingAt = 0;
  let pageHidden = false;
  let pageEpoch = 0;
  const pageLeaving = () => pageHidden || Date.now() - leavingAt < LEAVING_MS;
  window.addEventListener('beforeunload', () => {
    leavingAt = Date.now();
  });
  window.addEventListener('pagehide', () => {
    pageHidden = true;
    pageEpoch += 1;
  });
  window.addEventListener('pageshow', () => {
    pageHidden = false;
    leavingAt = 0;
  });

  /* Wiring */

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-cb-add-form]')) return;
    if (event.defaultPrevented || !window.fetch || !window.FormData) return;
    // Cards on the basket page send the plain form: Shopify reloads the basket with the new line.
    if (form.closest('[data-cb-native-add]')) return;
    // No drawer on this page (Cart type Page): Shopify's own add and basket page are the confirmation.
    if (!cartDrawer()) return;
    event.preventDefault();
    add(form, event.submitter || null);
  });

  // Product cards with a size choice: the card shows the chosen size's price (and subscription price).
  const showCardSize = (input) => {
    const card = input.closest('.cb-card');
    if (!card) return;
    const amount = card.querySelector('[data-cb-card-amount]');
    const price = input.getAttribute('data-cb-price');
    if (amount && price && amount.textContent !== price) amount.textContent = price;
    // Sitewide sale (snippets/cb-sale.liquid): the card's sale block shows the chosen size's sale price.
    const sale = card.querySelector('.cb-card__price [data-cb-sale-block]');
    const salePrice = input.getAttribute('data-cb-sale-price');
    if (sale && price && salePrice && window.CBSale) window.CBSale.fill(sale, price, salePrice);
    const sub = card.querySelector('[data-cb-card-sub]');
    if (sub && input.hasAttribute('data-cb-sub')) {
      const text = input.getAttribute('data-cb-sub') || '';
      if (sub.textContent !== text) sub.textContent = text;
      sub.hidden = !text;
    }
  };

  // Every card, from the size that is actually chosen: browsers restore a chosen size on back/forward and
  // reload (form state) without a change event, while the price is the page's original one.
  const syncCardSizes = () => {
    document.querySelectorAll('input[data-cb-card-variant]:checked').forEach(showCardSize);
  };

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-cb-card-variant]') || !input.checked) return;
    showCardSize(input);
    const form = input.form;
    if (form) clearError(form);
  });

  const whenSettled = (event, handler) => {
    const pending = event && event.promise;
    if (!pending || typeof pending.then !== 'function') return;
    pending.then(handler, () => {});
  };

  // Horizon's own cart (drawer and /cart page: quantity, remove, quick add) changes the basket: keep the
  // header count right, and redraw the free delivery lines from the new total. A successful Horizon add
  // opens the drawer by itself (auto-open): announce it and keep focus in the drawer.
  document.addEventListener(LINES_UPDATE, (event) => {
    if (ownEvents.has(event)) return;
    const fromHorizonAdd = event && event.action === 'add';
    whenSettled(event, (result) => {
      const total = result && result.cart ? result.cart.totalQuantity : null;
      if (typeof total === 'number') updateCount(total);
      const detail = result && result.detail;
      const added = fromHorizonAdd && detail && detail.didError === false;
      if (added && drawerAutoOpens(drawerReady())) {
        refresh().catch(() => {});
        announceInDrawer(drawerAddedText(null));
        guardDrawerFocus();
        return;
      }
      if (typeof total !== 'number' || hasFreeDelivery()) refresh().catch(() => {});
    });
  });

  // A discount code changes the total after discounts, so the free delivery amount too.
  document.addEventListener('shopify:cart:discount-update', (event) => {
    if (!hasFreeDelivery()) return;
    whenSettled(event, () => refresh().catch(() => {}));
  });

  // Back/forward: the page may show an old count, an old free delivery line and a busy button.
  // Safari and Firefox restore from the back/forward cache (persisted); Chrome often rebuilds the
  // page from the HTTP cache instead (navigation type back_forward).
  window.addEventListener('pageshow', (event) => {
    syncCardSizes();
    if (event.persisted) {
      document.querySelectorAll('form[data-cb-busy]').forEach((form) => {
        form.removeAttribute('data-cb-busy');
        form.querySelectorAll('[aria-busy="true"]').forEach((button) => setBusy(form, button, false));
      });
    }
    let backForward = false;
    try {
      const entry = window.performance && performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      backForward = !!entry && entry.type === 'back_forward';
    } catch (error) {
      backForward = false;
    }
    if (event.persisted || backForward) refresh().catch(() => {});
  });

  const init = () => {
    ensureRegions();
    wireBasketLinks();
    watchDrawerLayout();
    if (cartDrawer()) loadStandardEvents();
    syncCardSizes();
  };
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init, { once: true });

  window.cbCart = {
    refresh,
    updateCount,
    add: (form) => add(form, null),
    renderFreeDelivery,
    formatMoney,
  };
})();
